import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, Moon, Sun } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const links = [
  { id: "#immobilien", label: "01 · Immobilien", testid: "nav-link-immobilien" },
  { id: "#photovoltaik", label: "02 · Photovoltaik", testid: "nav-link-photovoltaik" },
  { id: "#kunst", label: "03 · Kunst", testid: "nav-link-kunst" },
  { id: "#vergleich", label: "Vergleich", testid: "nav-link-vergleich" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [dark, setDark] = useState(false);

  useEffect(() => {
    setDark(document.documentElement.dataset.theme === "dark");
  }, []);

  const toggleTheme = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.dataset.theme = next ? "dark" : "light";
    localStorage.setItem("fiplago-theme", next ? "dark" : "light");
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="nav-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-paper/85 backdrop-blur-xl border-b border-petrol/10" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-8 lg:px-16 h-[72px]">
        <button
          data-testid="nav-logo"
          onClick={() => go("#top")}
          className="flex items-center gap-2.5 mr-10"
          aria-label="Zum Seitenanfang"
        >
          <span className="block h-3 w-3 bg-gold" />
          <span className="font-dune text-xl text-petrol">FIPLAGO</span>
          <span className="hidden xl:block whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 mt-1">Kanzlei für Finanzplanung</span>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={l.testid}
              onClick={() => go(l.id)}
              className="whitespace-nowrap font-mono text-xs uppercase tracking-[0.15em] text-slate-600 hover:text-petrol transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="theme-toggle"
            onClick={toggleTheme}
            aria-label="Farbschema wechseln"
            title={dark ? "Helle Version anzeigen" : "Dunkle Version (Bronze) anzeigen"}
            className="p-2 text-slate-600 hover:text-petrol transition-colors duration-300"
          >
            {dark ? <Sun size={17} /> : <Moon size={17} />}
          </button>
          <button
            data-testid="nav-cta-immobilien-check"
            onClick={() => go("#check")}
            className="group inline-flex items-center gap-2 whitespace-nowrap bg-petrol px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.1em] text-paper transition-colors duration-300 hover:bg-petrol-dark"
          >
            Immobilien-Check anfordern
            <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </nav>

        <div className="flex items-center gap-1 lg:hidden">
          <button
            data-testid="theme-toggle-mobile"
            onClick={toggleTheme}
            aria-label="Farbschema wechseln"
            className="p-2 text-petrol"
          >
            {dark ? <Sun size={19} /> : <Moon size={19} />}
          </button>
          <button
            data-testid="nav-mobile-toggle"
            className="text-petrol p-2"
            onClick={() => setOpen(!open)}
            aria-label="Menü öffnen"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-paper/95 backdrop-blur-xl border-b border-petrol/10"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {links.map((l) => (
                <button
                  key={l.id}
                  data-testid={`${l.testid}-mobile`}
                  onClick={() => go(l.id)}
                  className="py-3 text-left font-mono text-sm uppercase tracking-[0.15em] text-slate-700 hover:text-petrol border-b border-petrol/10"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="nav-cta-immobilien-check-mobile"
                onClick={() => go("#check")}
                className="mt-4 bg-petrol px-5 py-3.5 text-center text-xs font-semibold uppercase tracking-[0.1em] text-paper"
              >
                Immobilien-Check anfordern
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
